import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart, Shield, Users, Star, Home as HomeIcon,
  ShoppingBag, Calendar, Coffee, Smile, MapPin,
  Clock, UserCheck, Sparkles, HandHeart, ArrowRight,
  Quote, Utensils, Gamepad2, Smartphone
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

// Hero: Two elderly women walking together from behind
const HERO_IMAGE = "https://images.unsplash.com/photo-1694857561361-b66b3d1a6cc8?w=800&auto=format&fit=crop&q=80";
// About: Two people walking arm in arm in snowy park
const ABOUT_IMAGE = "https://images.unsplash.com/photo-1768192655316-22a5eee66e28?w=600&auto=format&fit=crop&q=80";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true }
};

const trustItems = [
  { icon: Shield, text: 'Premium Companionship' },
  { icon: Heart, text: 'Non-Regulated Lifestyle Support' },
  { icon: Users, text: 'Trusted & Compassionate Team' },
  { icon: Star, text: 'Tailored to Individual Needs' },
];

const services = [
  { icon: Coffee, title: 'Social Visits & Companionship', description: 'Warm, friendly visits in the comfort of home, offering conversation and connection.' },
  { icon: MapPin, title: 'Outings & Community Engagement', description: 'Accompanied trips to local events, parks, cafes, and community activities.' },
  { icon: ShoppingBag, title: 'Errands & Practical Help', description: 'Shopping, post office runs, prescription collection, and everyday errands.' },
  { icon: Utensils, title: 'Light Home Support', description: 'Tidying, making tea and snacks, light cleaning, and laundry assistance.' },
  { icon: Gamepad2, title: 'Cognitive & Engagement Activities', description: 'Board games, puzzles, reading together, and light memory exercises.' },
  { icon: Smartphone, title: 'Tech & Family Connection', description: 'Help using WhatsApp, video calling family, and sharing messages and photos.' },
];

const whyChoose = [
  { icon: Heart, title: 'Personal & Warm Approach', description: 'Every interaction is built on genuine care and respect.' },
  { icon: Star, title: 'Premium Experience', description: 'Quality service that exceeds expectations.' },
  { icon: Clock, title: 'Flexible Plans', description: 'Companionship that fits your schedule and needs.' },
  { icon: Shield, title: 'Dignity & Trust', description: 'Your wellbeing and privacy always come first.' },
  { icon: Users, title: 'Community-Minded Values', description: 'We believe in the power of connection.' },
  { icon: Smile, title: 'Focus on Wellbeing', description: 'Supporting independence and happiness.' },
];

const companions = [
  {
    name: 'Sarah Mitchell',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1648414749870-282b53b1548f?w=400&auto=format&fit=crop&q=80',
    bio: 'Warm and patient, Sarah brings years of experience in community support and a genuine love for meaningful conversations.'
  },
  {
    name: 'James Thompson',
    role: 'Companion',
    image: 'https://images.pexels.com/photos/5698208/pexels-photo-5698208.jpeg?w=400&auto=compress&cs=tinysrgb',
    bio: 'Friendly and reliable, James enjoys helping others stay active and engaged with their communities.'
  },
  {
    name: 'Emma Clarke',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1605888357467-9caf88c89f67?w=400&auto=format&fit=crop&q=80',
    bio: 'Compassionate and attentive, Emma creates a comfortable environment where everyone feels valued.'
  },
  {
    name: 'Michael Patel',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1710952615354-3e5e9f234fc7?w=400&auto=format&fit=crop&q=80',
    bio: 'Dedicated and thoughtful, Michael brings energy and warmth to every visit he makes.'
  },
];

const pricing = [
  { name: 'Companionship Visit', duration: '1 hour', price: '£20' },
  { name: 'Extended Visit', duration: '2 hours', price: '£38' },
  { name: 'Half-Day Support', duration: '4 hours', price: '£75' },
  { name: 'Outings / Appointments', duration: 'Variable', price: '£25' },
];

const testimonials = [
  {
    quote: "AERW Lifestyle has been wonderful for my mother. The companionship visits have given her something to look forward to each week. The team is so respectful and kind.",
    author: "Rebecca H.",
    location: "London"
  },
  {
    quote: "Finding trustworthy companionship support was such a relief. The service is professional yet feels genuinely personal. Highly recommend to anyone seeking quality support.",
    author: "David M.",
    location: "Birmingham"
  },
  {
    quote: "The companions are exceptional - patient, friendly, and always reliable. It's given our family peace of mind knowing Dad has such wonderful support.",
    author: "Sarah J.",
    location: "Manchester"
  },
];

export const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F7F9FC] via-white to-[#F7F9FC]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
                Premium Lifestyle Companionship
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
                Meaningful Companionship for Everyday Wellbeing
              </h1>
              <p className="text-lg text-[#4A5568] leading-relaxed mb-8 max-w-xl">
                AERW Lifestyle provides premium non-regulated companionship services across the United Kingdom,
                supporting wellbeing, independence, social connection, and confidence through trusted everyday companionship.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact#consultation-form"
                  className="inline-flex items-center justify-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
                >
                  Book a Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>

                <Link
                  to="/services"
                  className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC] transition-colors"
                >
                  View Services
                </Link>
              
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-[#3A7BD5]/10 rounded-3xl transform rotate-3" />
          <img
            src={HERO_IMAGE}
            alt="Companionship and connection"
            className="relative rounded-3xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
            data-testid="hero-image"
          />
        </motion.div>
    </div>
        </div >
      </section >

 {/* Trust Strip */}
<section className="py-8 bg-white border-y border-[#E2E8F0]" data-testid="trust-strip">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
      {trustItems.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          className="flex items-center gap-3"
        >
            <div className="w-10 h-10 bg-[#F7F9FC] rounded-xl flex items-center justify-center flex-shrink-0">
              <item.icon className="w-5 h-5 text-[#3A7BD5]" />
            </div>
            <span className="text-sm font-medium text-[#1A1A1A]">{item.text}</span>
          </motion.div>
        ))}
      </div>
    </div>
      </section >

  {/* About Snapshot */ }
  < section className = "py-20 md:py-28 bg-white" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="order-2 lg:order-1"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-[#1E4F8A]/5 rounded-3xl transform -rotate-3" />
            <img
              src={ABOUT_IMAGE}
              alt="About AERW Lifestyle"
              className="relative rounded-3xl shadow-xl w-full h-[400px] object-cover"
            />
          </div>
        </motion.div>
        <motion.div {...fadeInUp} className="order-1 lg:order-2">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
            About Us
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
            Premium Companionship Built on Trust and Warmth
          </h2>
          <p className="text-[#4A5568] leading-relaxed mb-6">
            AERW Lifestyle is a premium companionship provider focused on dignity, trust, emotional wellbeing,
            social engagement, and independence. We understand that meaningful connection is essential to a
            fulfilling life.
          </p>
          <p className="text-[#4A5568] leading-relaxed mb-8">
            Our compassionate companions provide non-regulated lifestyle support, helping individuals stay
            connected with their communities, maintain their independence, and enjoy everyday moments with
            warmth and genuine care.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center text-[#1E4F8A] font-medium hover:text-[#3A7BD5] transition-colors"
            data-testid="about-link"
          >
            Learn more about us
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </motion.div>
      </div>
    </div>
      </section >

  {/* Services Overview */ }
  < section className = "py-20 md:py-28 bg-[#F7F9FC]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeInUp} className="text-center mb-16">
        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
          Our Services
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
          Companionship Tailored to Your Lifestyle
        </h2>
        <p className="text-[#4A5568] max-w-2xl mx-auto">
          From friendly home visits to accompanied outings, we provide comprehensive non-regulated
          lifestyle support that makes a real difference.
        </p>
      </motion.div>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
          >
            <Card className="h-full bg-white border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#F7F9FC] rounded-2xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#3A7BD5]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{service.title}</h3>
                <p className="text-[#4A5568] leading-relaxed">{service.description}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
      <motion.div {...fadeInUp} className="text-center mt-12">
        <Link
          to="/services"
          className="inline-flex items-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
          data-testid="services-cta"
        >
          Explore All Services
          <ArrowRight className="w-5 h-5 ml-2" />
        </Link>
      </motion.div>
    </div>
      </section >

  {/* Why Choose Us */ }
  < section className = "py-20 md:py-28 bg-white" data-testid="why-choose-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeInUp} className="text-center mb-16">
        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
          Why Choose Us
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
          What Makes AERW Lifestyle Different
        </h2>
        <p className="text-[#4A5568] max-w-2xl mx-auto">
          We're committed to providing premium companionship that truly makes a difference in people's lives.
        </p>
      </motion.div>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {whyChoose.map((item, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className="flex gap-4"
          >
            <div className="w-12 h-12 bg-[#F7F9FC] rounded-xl flex items-center justify-center flex-shrink-0">
              <item.icon className="w-6 h-6 text-[#3A7BD5]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1A1A1A] mb-2">{item.title}</h3>
              <p className="text-sm text-[#4A5568]">{item.description}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
      </section >

  {/* Meet Your Companions Preview */ }
  < section className = "py-20 md:py-28 bg-[#F7F9FC]" data-testid="companions-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeInUp} className="text-center mb-16">
        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
          Our Team
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
          Meet Your Companions
        </h2>
        <p className="text-[#4A5568] max-w-2xl mx-auto">
          Our carefully selected companions bring warmth, reliability, and genuine care to every interaction.
        </p>
      </motion.div>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true }}
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {companions.map((companion, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
          >
            <Card className="h-full bg-white border-[#E2E8F0] overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={companion.image}
                  alt={companion.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-[#1A1A1A] mb-1">{companion.name}</h3>
                <p className="text-sm text-[#3A7BD5] mb-3">{companion.role}</p>
                <p className="text-sm text-[#4A5568] leading-relaxed">{companion.bio}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
      <motion.div {...fadeInUp} className="text-center mt-12">
        <Link
          to="/companions"
          className="inline-flex items-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#1E4F8A] hover:text-white transition-colors"
          data-testid="companions-cta"
        >
          Meet the Full Team
          <ArrowRight className="w-5 h-5 ml-2" />
        </Link>
      </motion.div>
    </div>
      </section >

  {/* Pricing Preview */ }
  < section className = "py-20 md:py-28 bg-white" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeInUp} className="text-center mb-16">
        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
          Pricing
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
          Transparent & Fair Pricing
        </h2>
        <p className="text-[#4A5568] max-w-2xl mx-auto">
          Quality companionship at accessible rates. All prices are starting points and may vary depending on location and requirements.
        </p>
      </motion.div>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true }}
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto"
      >
        {pricing.map((item, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
          >
            <Card className="h-full bg-[#F7F9FC] border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{item.name}</h3>
                <p className="text-sm text-[#4A5568] mb-4">{item.duration}</p>
                <p className="text-3xl font-bold text-[#1E4F8A]">
                  From {item.price}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
      <motion.div {...fadeInUp} className="text-center mt-8">
        <p className="text-sm text-[#4A5568] mb-6">
          Tailored packages available. Contact us for bespoke support plans.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center text-[#1E4F8A] font-medium hover:text-[#3A7BD5] transition-colors"
          data-testid="pricing-link"
        >
          View full pricing details
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </motion.div>
    </div>
      </section >

  {/* Testimonials */ }
  < section className = "py-20 md:py-28 bg-[#1E4F8A]" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeInUp} className="text-center mb-16">
        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
          Testimonials
        </span>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
          What Families Say
        </h2>
      </motion.div>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        whileInView="whileInView"
        viewport={{ once: true }}
        className="grid md:grid-cols-3 gap-8"
      >
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
          >
            <Card className="h-full bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-8">
                <Quote className="w-10 h-10 text-[#3A7BD5] mb-4" />
                <p className="text-white/90 leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-white">{testimonial.author}</p>
                  <p className="text-sm text-white/70">{testimonial.location}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
      </section >

  {/* Final CTA */ }
  < section className = "py-20 md:py-28 bg-white" data-testid="final-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div {...fadeInUp}>
        <HandHeart className="w-16 h-16 text-[#3A7BD5] mx-auto mb-6" />
        <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
          Ready to Experience Premium Companionship?
        </h2>
        <p className="text-lg text-[#4A5568] mb-8 max-w-2xl mx-auto">
          Take the first step towards meaningful companionship and lifestyle support.
          We're here to help you or your loved ones stay connected, confident, and supported.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/contact#consultation-form"
            className="inline-flex items-center justify-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66]"
          >
            Book a Free Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>

          <a
            href="tel:07803174495"
            className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC]"
          >
            Call 07803 174495
          </a>
        </div>
      </motion.div>
    </div>
      </section >
    </div >
  );
};

export default HomePage;
